<template>
  <sidebar-cube-link class="cube" :to="routerLinkTo">
    <img
      class="cube__img"
      width="60"
      height="60"
      :src="icon"
      :alt="productionType"
    />
    <div class="cube__text">
      <div class="cube__title">
        <span class="cube__title-text">{{ cube.params.nickName }}</span>
        <template v-if="hasFreeCapacity">
          <Bolt
            :title="
              t('efriends.hasFreeCapacity', { '@cube': cube.params.nickName })
            "
          />
        </template>
      </div>
      <ul class="cube__subtitle">
        <li v-if="cube.productionType">{{ productionType }}</li>
        <li v-if="power && power > 0">{{ power }} kWp</li>
        <li v-if="price">{{ price }} ct</li>
        <li v-if="distance">
          <Distance :distance="distance" />
        </li>
        <li v-if="numLikes">
          {{ numLikes }} <HeartFull :extra-classes="['cube__heart__icon']" />
        </li>
      </ul>
    </div>
  </sidebar-cube-link>
</template>
<script setup>
  import SidebarCubeLink from '@/components/efriends/SidebarCubeLink.vue'
  import Bolt from '@/components/icons/Bolt.vue'
  import Distance from '@/components/Distance.vue'
  import HeartFull from '@/components/icons/HeartFull.vue'
  import { computed } from 'vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useMapStore } from '@/stores/useMapStore'

  const props = defineProps({
    cube: {
      type: Object,
      required: true,
    },
  })

  const mapStore = useMapStore()
  const { t } = useStrings()

  const icon = computed(() => mapStore.cubeIconUrl(props.cube, 'svg'))
  const productionType = computed(() => t(`productionTypes.${props.cube.productionType}`))
  const power = computed(() =>
    Math.round(props.cube.params.productionCapacityKWp)
  )
  const price = computed(() => {
    if (!props.cube.hasProduction) {
      return
    }
    const prices = mapStore.getProducerPrices(props.cube.cubeName)
    if (!prices) {
      return
    }
    return (parseFloat(prices.currentPrice) * 100).toFixed(1)
  })
  const hasFreeCapacity = computed(
    () => props.cube.params.openConnectionsAvailable >= 1
  )
  const distance = computed(() => props.cube.distance)
  const numLikes = computed(() => props.cube.numLikes)

  let routerLinkTo = {
    name: 'map',
    params: {
      selected: props.cube.cubeName,
    },
  }
</script>

<style lang="scss" scoped>
  .cube {
    display: flex;
    align-items: center;
    width: 100%;
    appearance: none;
    background: none;
    border: 0;
    padding: 0;
    color: $grey-darker;
    text-align: left;
  }

  .cube__img {
    height: $cube-list-img-size-min;
    width: $cube-list-img-size-min;
    margin-right: 0.75em;
    flex-shrink: 0;
  }

  .cube__title {
    margin-bottom: 0.3em;
    font-weight: bold;
    line-height: $line-height-min;

    > .icon {
      height: 1rem;
      width: 1rem;
      margin-bottom: -0.12em;
      border-radius: 50%;
      // Using rem here leads to random rounding errors
      padding: 4px 2px 3px 3px;
      color: #fff;
      background-color: $grey;
    }
  }

  .cube__title-text {
    display: inline-block;
    flex-basis: 0;
    margin-right: 0.3em;
  }

  .cube__subtitle {
    display: flex;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
    list-style: none;
    color: $grey-dark;
    font-size: 0.9em;
    line-height: $line-height-sm;

    > *:not(:last-child) {
      &::after {
        content: '·';
        display: inline-block;
        padding: 0 0.5em;
      }
    }
  }
  .cube__heart__icon {
    color: $red;
    vertical-align: baseline;
  }
</style>
